import { ScriptBlockSource } from "@prisma/client";
import { TFn } from "@/app/translations/TFn";
import { TKey } from "@/app/translations/TKey";
import { StandardCallType } from "@/app/types/call/StandardCallType";
import { PlaceholderRow } from "@/app/types/common/PlaceholderRow";
import {
  ScriptBlockData,
  ScriptItemData,
  ScriptItemInput,
  ScriptSourceItem,
  ScriptWorkspace,
} from "@/app/types/messages-content/ScriptData";
import { STANDARD_SCRIPT_BLOCKS } from "@/app/types/messages-content/StandardScriptBlock";
import { Limits } from "@/app/vars/Limits";

export function callTypeLabel(t: TFn, type: string): string {
  const isStandard = Object.values(StandardCallType).includes(type as StandardCallType);
  return isStandard ? t(`CALL_TYPE_${type}` as TKey) : type;
}

export function buildCallTypeOptions(t: TFn, projectTypes: string[]): [string, string][] {
  const types = Array.from(new Set([...Object.values(StandardCallType), ...projectTypes]));
  return types.map((type) => [type, callTypeLabel(t, type)]);
}

export function countCustomCallTypes(projectTypes: string[]): number {
  const standard = Object.values(StandardCallType) as string[];
  return projectTypes.filter((type) => !standard.includes(type)).length;
}

export function buildScriptBlocks(workspace?: ScriptWorkspace): ScriptBlockData[] {
  return STANDARD_SCRIPT_BLOCKS.map((shape, position) => {
    const stored = workspace?.script?.blocks.find((block) => block.type === shape.type);
    const seeds = shape.source ? readSourceItems(workspace, shape.source) : [];

    const items =
      stored && stored.items.length > 0
        ? stored.items
        : seeds.map((seed, itemPosition) => ({
            id: -(itemPosition + 1),
            label: seed.label,
            response: null,
            position: itemPosition,
            sourceInsightId: seed.insightId,
          }));

    return {
      id: stored?.id ?? -(position + 1),
      type: shape.type,
      kind: shape.kind,
      source: shape.source,
      body: stored?.body ?? null,
      position,
      items,
    };
  });
}

export function readSourceItems(
  workspace: ScriptWorkspace | undefined,
  source: ScriptBlockSource,
): ScriptSourceItem[] {
  return workspace?.sources.find((group) => group.source === source)?.items ?? [];
}

const PLACEHOLDER_WIDTHS = ["92%", "76%", "88%", "70%", "84%", "80%"];

export function buildScriptPlaceholderRows(count: number): PlaceholderRow[] {
  return Array.from({ length: count }, (_, index) => ({
    cells: [{ width: PLACEHOLDER_WIDTHS[index % PLACEHOLDER_WIDTHS.length] }],
  }));
}

export function buildOptimisticWorkspace(
  workspace: ScriptWorkspace | undefined,
  blocks: ScriptBlockData[],
  callType: string,
): ScriptWorkspace {
  return {
    script: {
      id: workspace?.script?.id ?? 0,
      callType,
      needUpdate: workspace?.script?.needUpdate ?? false,
      avatarUpdatedAt: workspace?.script?.avatarUpdatedAt ?? null,
      blocks,
    },
    sources: workspace?.sources ?? [],
    canRefreshSources: workspace?.canRefreshSources ?? false,
  };
}

function nextTempId(blocks: ScriptBlockData[]): number {
  return Math.min(0, ...blocks.flatMap((block) => block.items.map((item) => item.id))) - 1;
}

function replaceItems(
  blocks: ScriptBlockData[],
  blockType: string,
  items: ScriptItemData[],
): ScriptBlockData[] {
  return blocks.map((block) =>
    block.type === blockType
      ? { ...block, items: items.map((item, position) => ({ ...item, position })) }
      : block,
  );
}

export function withBlockBody(
  blocks: ScriptBlockData[],
  blockType: string,
  body: string,
): ScriptBlockData[] {
  return blocks.map((block) =>
    block.type === blockType
      ? { ...block, body: body.slice(0, Limits.MAX_SCRIPT_BLOCK_BODY) }
      : block,
  );
}

export function withBlockItems(
  blocks: ScriptBlockData[],
  blockType: string,
  inputs: ScriptItemInput[],
): ScriptBlockData[] {
  const block = blocks.find((entry) => entry.type === blockType);
  const shape = STANDARD_SCRIPT_BLOCKS.find((entry) => entry.type === blockType);
  if (!block || !shape) {
    return blocks;
  }

  const byId = new Map(block.items.map((item) => [item.id, item]));
  const pinned = shape.source ? block.items.filter((item) => item.sourceInsightId !== null) : [];
  const pinnedIds = new Set(pinned.map((item) => item.id));
  const baseTempId = nextTempId(blocks);

  const wanted = inputs
    .map((input) => ({
      id: input.id,
      label: input.label.trim().slice(0, Limits.MAX_SCRIPT_ITEM_LABEL),
    }))
    .filter((input) => input.label.length > 0)
    .slice(0, shape.maxItems)
    .filter((input) => input.id === null || !pinnedIds.has(input.id))
    .map((input, index) => {
      const existing = input.id === null ? undefined : byId.get(input.id);
      if (existing) {
        return { ...existing, label: input.label };
      }
      return {
        id: baseTempId - index,
        label: input.label,
        response: null,
        position: 0,
        sourceInsightId: null,
      };
    });

  return replaceItems(blocks, blockType, [...pinned, ...wanted]);
}

export function withItemForInsight(
  blocks: ScriptBlockData[],
  blockType: string,
  insightId: number,
  label: string,
): ScriptBlockData[] {
  const block = blocks.find((entry) => entry.type === blockType);
  const shape = STANDARD_SCRIPT_BLOCKS.find((entry) => entry.type === blockType);
  if (!block || !shape) {
    return blocks;
  }

  const trimmed = label.trim().slice(0, Limits.MAX_SCRIPT_ITEM_LABEL);
  const existing = block.items.find((item) => item.sourceInsightId === insightId);

  if (!trimmed) {
    return replaceItems(
      blocks,
      blockType,
      block.items.filter((item) => item.sourceInsightId !== insightId),
    );
  }

  if (existing) {
    return replaceItems(
      blocks,
      blockType,
      block.items.map((item) => (item === existing ? { ...item, label: trimmed } : item)),
    );
  }

  if (block.items.length >= shape.maxItems) {
    return blocks;
  }

  return replaceItems(blocks, blockType, [
    ...block.items,
    {
      id: nextTempId(blocks),
      label: trimmed,
      response: null,
      position: block.items.length,
      sourceInsightId: insightId,
    },
  ]);
}

export function withItemResponse(
  blocks: ScriptBlockData[],
  blockType: string,
  label: string,
  response: string,
): ScriptBlockData[] {
  const block = blocks.find((entry) => entry.type === blockType);
  const target = block?.items.find((item) => item.label === label);
  if (!block || !target) {
    return blocks;
  }

  const trimmed = response.trim().slice(0, Limits.MAX_SCRIPT_ITEM_RESPONSE);

  return replaceItems(
    blocks,
    blockType,
    block.items.map((item) => (item === target ? { ...item, response: trimmed || null } : item)),
  );
}
